import { supabase } from '../supabase.ts';

/**
 * Locations, their opening hours, licences and registers.
 *
 * Read and written straight from the browser under RLS: every table here is
 * gated on `locations.manage` (licences on `compliance.manage`), so the
 * database is the authority and this module only shapes the rows.
 *
 * `location_hours.slot` and the pickup templates are Postgres `timerange`
 * values. PostgREST hands them over as the literal `[09:00:00,21:00:00)`, and
 * that is also what it accepts back, so the boundary conversion lives here
 * rather than in every form.
 */

export type LocationType = 'store' | 'warehouse' | 'delivery_hub' | 'vehicle';

export interface LocationRecord {
  id: string;
  name: string;
  slug: string;
  type: LocationType;
  active: boolean;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  timezone: string;
  latitude: number | null;
  longitude: number | null;
  allowsPickup: boolean;
  allowsDelivery: boolean;
  allowsCurbside: boolean;
  allowsDriveThrough: boolean;
  allowsWalkIn: boolean;
  allowsKiosk: boolean;
}

const LOCATION_COLUMNS =
  'id, name, slug, type, active, address_line1, address_line2, city, state, zip, country, phone, email, timezone, latitude, longitude, allows_pickup, allows_delivery, allows_curbside, allows_drive_through, allows_walk_in, allows_kiosk';

interface LocationRow {
  id: string;
  name: string;
  slug: string;
  type: LocationType;
  active: boolean;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  timezone: string;
  latitude: number | null;
  longitude: number | null;
  allows_pickup: boolean;
  allows_delivery: boolean;
  allows_curbside: boolean;
  allows_drive_through: boolean;
  allows_walk_in: boolean;
  allows_kiosk: boolean;
}

function toLocation(row: LocationRow): LocationRecord {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    type: row.type,
    active: row.active,
    addressLine1: row.address_line1,
    addressLine2: row.address_line2,
    city: row.city,
    state: row.state,
    zip: row.zip,
    country: row.country,
    phone: row.phone,
    email: row.email,
    timezone: row.timezone,
    latitude: row.latitude,
    longitude: row.longitude,
    allowsPickup: row.allows_pickup,
    allowsDelivery: row.allows_delivery,
    allowsCurbside: row.allows_curbside,
    allowsDriveThrough: row.allows_drive_through,
    allowsWalkIn: row.allows_walk_in,
    allowsKiosk: row.allows_kiosk,
  };
}

export async function listLocations(accountId: string): Promise<LocationRecord[]> {
  const { data, error } = await supabase
    .from('locations')
    .select(LOCATION_COLUMNS)
    .eq('account_id', accountId)
    .order('type', { ascending: true })
    .order('name', { ascending: true });

  if (error) throw error;

  return ((data ?? []) as unknown as LocationRow[]).map(toLocation);
}

export type LocationInput = Omit<LocationRecord, 'id'>;

function toRow(input: LocationInput) {
  return {
    name: input.name,
    slug: input.slug,
    type: input.type,
    active: input.active,
    address_line1: input.addressLine1,
    address_line2: input.addressLine2,
    city: input.city,
    state: input.state,
    zip: input.zip,
    country: input.country,
    phone: input.phone,
    email: input.email,
    timezone: input.timezone,
    latitude: input.latitude,
    longitude: input.longitude,
    allows_pickup: input.allowsPickup,
    allows_delivery: input.allowsDelivery,
    allows_curbside: input.allowsCurbside,
    allows_drive_through: input.allowsDriveThrough,
    allows_walk_in: input.allowsWalkIn,
    allows_kiosk: input.allowsKiosk,
  };
}

export async function createLocation(
  accountId: string,
  input: LocationInput,
): Promise<LocationRecord> {
  const { data, error } = await supabase
    .from('locations')
    .insert({ account_id: accountId, ...toRow(input) })
    .select(LOCATION_COLUMNS)
    .single();

  if (error) throw error;

  return toLocation(data as unknown as LocationRow);
}

export async function updateLocation(
  accountId: string,
  id: string,
  input: LocationInput,
): Promise<LocationRecord> {
  const { data, error } = await supabase
    .from('locations')
    .update(toRow(input))
    .eq('account_id', accountId)
    .eq('id', id)
    .select(LOCATION_COLUMNS)
    .single();

  if (error) throw error;

  return toLocation(data as unknown as LocationRow);
}

/**
 * What a location is still carrying.
 *
 * Deleting one would orphan stock and history, and the foreign keys refuse it
 * with a constraint name nobody can act on. Counting first turns that into a
 * sentence: deactivate instead, or move the stock.
 */
export async function locationUsage(accountId: string, id: string) {
  const [inventory, orders, sessions, registers] = await Promise.all([
    supabase
      .from('inventory_levels')
      .select('variant_id', { count: 'exact', head: true })
      .eq('account_id', accountId)
      .eq('location_id', id),
    supabase
      .from('orders')
      .select('id', { count: 'exact', head: true })
      .eq('account_id', accountId)
      .eq('location_id', id),
    // Sessions hang off a register, which hangs off the location.
    supabase
      .from('register_sessions')
      .select('id, registers!inner(location_id)', { count: 'exact', head: true })
      .eq('account_id', accountId)
      .eq('registers.location_id', id),
    supabase
      .from('registers')
      .select('id', { count: 'exact', head: true })
      .eq('account_id', accountId)
      .eq('location_id', id),
  ]);

  return {
    inventory: inventory.count ?? 0,
    orders: orders.count ?? 0,
    sessions: sessions.count ?? 0,
    registers: registers.count ?? 0,
  };
}

export async function deleteLocation(accountId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('locations')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/* Hours                                                                       */
/* -------------------------------------------------------------------------- */

export type HoursKind = 'open' | 'pickup' | 'delivery';

export const HOURS_KINDS: HoursKind[] = ['open', 'pickup', 'delivery'];

export const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export interface HoursRow {
  id: number;
  dayOfWeek: number;
  kind: HoursKind;
  opensAt: string;
  closesAt: string;
}

/** `[09:00:00,21:00:00)` -> `09:00` / `21:00`. */
export function parseTimeRange(slot: string): { opensAt: string; closesAt: string } | null {
  const match = /^[[(](\d{2}:\d{2})(?::\d{2})?,(\d{2}:\d{2})(?::\d{2})?[)\]]$/.exec(slot.trim());

  if (!match?.[1] || !match[2]) return null;

  return { opensAt: match[1], closesAt: match[2] };
}

/** Half-open, matching the exclusion constraint: 9-5 and 5-9 do not overlap. */
export function toTimeRange(opensAt: string, closesAt: string): string {
  return `[${opensAt},${closesAt})`;
}

export async function listHours(accountId: string, locationId: string): Promise<HoursRow[]> {
  const { data, error } = await supabase
    .from('location_hours')
    .select('id, day_of_week, kind, slot')
    .eq('account_id', accountId)
    .eq('location_id', locationId)
    .order('day_of_week', { ascending: true });

  if (error) throw error;

  return (
    (data ?? []) as unknown as Array<{
      id: number;
      day_of_week: number;
      kind: HoursKind;
      slot: string;
    }>
  ).flatMap((row) => {
    const parsed = parseTimeRange(String(row.slot));

    return parsed ? [{ id: row.id, dayOfWeek: row.day_of_week, kind: row.kind, ...parsed }] : [];
  });
}

export async function addHours(
  accountId: string,
  input: {
    locationId: string;
    dayOfWeek: number;
    kind: HoursKind;
    opensAt: string;
    closesAt: string;
  },
): Promise<void> {
  const { error } = await supabase.from('location_hours').insert({
    account_id: accountId,
    location_id: input.locationId,
    day_of_week: input.dayOfWeek,
    kind: input.kind,
    slot: toTimeRange(input.opensAt, input.closesAt),
  });

  if (error) throw error;
}

export async function deleteHours(accountId: string, id: number): Promise<void> {
  const { error } = await supabase
    .from('location_hours')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/* Licences                                                                    */
/* -------------------------------------------------------------------------- */

export type LicenseType =
  | 'retail'
  | 'delivery'
  | 'distribution'
  | 'cultivation'
  | 'manufacturing'
  | 'microbusiness'
  | 'event';

export const LICENSE_TYPES: LicenseType[] = [
  'retail',
  'delivery',
  'distribution',
  'cultivation',
  'manufacturing',
  'microbusiness',
  'event',
];

export interface LicenseRecord {
  id: string;
  licenseNumber: string;
  type: LicenseType;
  state: string;
  locationId: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  metrcFacilityLicense: string | null;
  active: boolean;
}

export async function listLicenses(accountId: string): Promise<LicenseRecord[]> {
  const { data, error } = await supabase
    .from('licenses')
    .select(
      'id, license_number, type, state, location_id, issued_at, expires_at, metrc_facility_license, active',
    )
    .eq('account_id', accountId)
    .order('license_number', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    licenseNumber: row.license_number,
    type: row.type as LicenseType,
    state: row.state,
    locationId: row.location_id,
    issuedAt: row.issued_at,
    expiresAt: row.expires_at,
    metrcFacilityLicense: row.metrc_facility_license,
    active: row.active,
  }));
}

export type LicenseInput = Omit<LicenseRecord, 'id'>;

export async function saveLicense(
  accountId: string,
  input: LicenseInput & { id?: string },
): Promise<void> {
  const row = {
    license_number: input.licenseNumber,
    type: input.type,
    state: input.state,
    location_id: input.locationId,
    issued_at: input.issuedAt,
    expires_at: input.expiresAt,
    metrc_facility_license: input.metrcFacilityLicense,
    active: input.active,
  };

  const { error } = input.id
    ? await supabase.from('licenses').update(row).eq('account_id', accountId).eq('id', input.id)
    : await supabase.from('licenses').insert({ account_id: accountId, ...row });

  if (error) throw error;
}

export async function deleteLicense(accountId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('licenses')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/* Registers                                                                   */
/* -------------------------------------------------------------------------- */

export interface RegisterRecord {
  id: string;
  name: string;
  locationId: string;
  active: boolean;
}

export async function listRegisters(accountId: string): Promise<RegisterRecord[]> {
  const { data, error } = await supabase
    .from('registers')
    .select('id, name, location_id, active')
    .eq('account_id', accountId)
    .order('name', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    locationId: row.location_id,
    active: row.active,
  }));
}

export async function saveRegister(
  accountId: string,
  input: { id?: string; name: string; locationId: string; active: boolean },
): Promise<void> {
  const row = { name: input.name, location_id: input.locationId, active: input.active };

  const { error } = input.id
    ? await supabase.from('registers').update(row).eq('account_id', accountId).eq('id', input.id)
    : await supabase.from('registers').insert({ account_id: accountId, ...row });

  if (error) throw error;
}

export async function deleteRegister(accountId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('registers')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}
